import React, { useState } from 'react';

import styled from 'styled-components';

import { Color } from '@assets/constants';
import { useCustomNavigate } from '@hooks';

const ChangeCalculatorPage = () => {
  const navigate = useCustomNavigate();
  const [amountDue, setAmountDue] = useState<string>('');
  const [amountReceived, setAmountReceived] = useState<string>('');
  const [dueCurrency, setDueCurrency] = useState<'BGN' | 'EUR'>('BGN');
  const [receivedCurrency, setReceivedCurrency] = useState<'BGN' | 'EUR'>(
    'BGN'
  );

  const BGN_TO_EUR = 1.95583;

  const calculateChange = () => {
    const due = parseFloat(amountDue);
    const received = parseFloat(amountReceived);

    if (isNaN(due) || isNaN(received)) {
      return null;
    }

    // Convert both amounts to BGN
    const dueInBGN = dueCurrency === 'EUR' ? due * BGN_TO_EUR : due;
    const receivedInBGN =
      receivedCurrency === 'EUR' ? received * BGN_TO_EUR : received;

    // Calculate change in BGN
    const changeInBGN = receivedInBGN - dueInBGN;

    // Convert change to EUR
    const changeInEUR = changeInBGN / BGN_TO_EUR;

    return {
      changeInBGN,
      changeInEUR
    };
  };

  const result = calculateChange();

  return (
    <Container>
      <Card>
        <Title>Калкулатор за ресто</Title>

        <InputGroup>
          <Label>Дължима сума</Label>
          <CurrencySelector>
            <Input
              type="number"
              step="0.01"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              placeholder="0.00"
            />
            <CurrencyToggle>
              <CurrencyButton
                $active={dueCurrency === 'BGN'}
                onClick={() => setDueCurrency('BGN')}
              >
                BGN
              </CurrencyButton>
              <CurrencyButton
                $active={dueCurrency === 'EUR'}
                onClick={() => setDueCurrency('EUR')}
              >
                EUR
              </CurrencyButton>
            </CurrencyToggle>
          </CurrencySelector>
        </InputGroup>

        <InputGroup>
          <Label>Получена сума</Label>
          <CurrencySelector>
            <Input
              type="number"
              step="0.01"
              value={amountReceived}
              onChange={(e) => setAmountReceived(e.target.value)}
              placeholder="0.00"
            />
            <CurrencyToggle>
              <CurrencyButton
                $active={receivedCurrency === 'BGN'}
                onClick={() => setReceivedCurrency('BGN')}
              >
                BGN
              </CurrencyButton>
              <CurrencyButton
                $active={receivedCurrency === 'EUR'}
                onClick={() => setReceivedCurrency('EUR')}
              >
                EUR
              </CurrencyButton>
            </CurrencyToggle>
          </CurrencySelector>
        </InputGroup>

        {result && (
          <ResultSection>
            <ResultTitle>Ресто за връщане</ResultTitle>
            <ResultValue $positive={result.changeInEUR >= 0}>
              {result.changeInEUR >= 0 ? '€' : '-€'}
              {Math.abs(result.changeInEUR).toFixed(2)}
            </ResultValue>
            <ResultSubtext>
              ({Math.abs(result.changeInBGN).toFixed(2)} лв)
            </ResultSubtext>
            {result.changeInEUR < 0 && (
              <WarningText>Клиентът дължи още пари</WarningText>
            )}
          </ResultSection>
        )}

        <ButtonGroup>
          <ActionButton
            onClick={() => {
              setAmountDue('');
              setAmountReceived('');
            }}
          >
            Изчисти
          </ActionButton>
          <ActionButton $primary onClick={() => navigate('/admin-panel')}>
            Назад
          </ActionButton>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

export default ChangeCalculatorPage;

const Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${Color.LIGHT_GRAY};
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 40px;
  max-width: 500px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${Color.BLACK};
  margin: 0 0 30px 0;
  text-align: center;
`;

const InputGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Color.DARK_GRAY};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  font-size: 18px;
  border: 2px solid ${Color.LIGHT_GRAY};
  border-radius: 12px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${Color.ACCENT};
    box-shadow: 0 0 0 3px rgba(250, 192, 0, 0.1);
  }

  &::placeholder {
    color: ${Color.MEDIUM_GRAY};
  }
`;

const CurrencySelector = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const CurrencyToggle = styled.div`
  display: flex;
  background: ${Color.LIGHT_GRAY};
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const CurrencyButton = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$active ? Color.ACCENT : 'transparent')};
  color: ${(props) => (props.$active ? Color.BLACK : Color.GRAY)};

  &:hover {
    background: ${(props) => (props.$active ? Color.ACCENT : Color.WHITE)};
    filter: ${(props) => (props.$active ? 'brightness(0.95)' : 'none')};
  }
`;

const ResultSection = styled.div`
  background: ${Color.WHITE};
  border: 3px solid ${Color.ACCENT};
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  margin: 30px 0;
`;

const ResultTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Color.BLACK};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  opacity: 0.8;
`;

const ResultValue = styled.div<{ $positive: boolean }>`
  font-size: 48px;
  font-weight: 700;
  color: ${(props) => (props.$positive ? Color.BLACK : Color.RED)};
  margin-bottom: 8px;
`;

const ResultSubtext = styled.div`
  font-size: 16px;
  color: ${Color.BLACK};
  opacity: 0.7;
`;

const WarningText = styled.div`
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${Color.RED};
  background: ${Color.LIGHT_GRAY};
  padding: 8px 16px;
  border-radius: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$primary ? Color.ACCENT : Color.WHITE)};
  color: ${Color.BLACK};
  border: 2px solid
    ${(props) => (props.$primary ? Color.ACCENT : Color.LIGHT_GRAY)};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    filter: ${(props) => (props.$primary ? 'brightness(0.95)' : 'none')};
  }

  &:active {
    transform: translateY(0);
  }
`;
